/* Jacked Jeans — conversion features
   1. Bundle progress bar refresh (cart drawer)
   2. Sticky add-to-cart bar on mobile
   3. Quick add drawer with size selection
   Written defensively: every feature is independent and fails quietly so a problem in one
   can never block add-to-cart. */
(function () {
  'use strict';

  var money = function (cents) {
    if (window.theme && theme.Currency && theme.settings && theme.settings.moneyFormat) {
      return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
    }
    return '$' + (cents / 100).toFixed(2);
  };

  /* ------------------------------------------------------------------
     1. Bundle progress bar
     ------------------------------------------------------------------ */
  function refreshBundleProgress() {
    var targets = document.querySelectorAll('[data-bundle-progress]');
    if (!targets.length) return;

    fetch('/?sections=bundle-progress', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !data['bundle-progress']) return;
        var parsed = new DOMParser().parseFromString(data['bundle-progress'], 'text/html');
        var fresh = parsed.querySelector('[data-bundle-progress]');
        if (!fresh) return;
        document.querySelectorAll('[data-bundle-progress]').forEach(function (el) {
          el.innerHTML = fresh.innerHTML;
        });
      })
      .catch(function () {});
  }

  // The theme fires cart:updated for quantity edits, but its drawer rebuild after an add
  // does not always reach that path, so listen to both and refresh once the drawer settles.
  document.addEventListener('cart:updated', refreshBundleProgress);
  document.addEventListener('ajaxProduct:added', function () {
    refreshBundleProgress();
    setTimeout(refreshBundleProgress, 700);
  });

  /* ------------------------------------------------------------------
     1b. Merged cart rows
     Shopify splits one variant across several cart lines when a Buy X Get Y discount
     covers part of the quantity. Those rows are merged for display, so quantity has to
     be set per variant via /cart/update.js — a single line key would only move one line.
     Only merged rows are handled here; every other row keeps the theme's own logic.
     ------------------------------------------------------------------ */
  function setMergedQuantity(row, newQty) {
    var target = Math.max(0, newQty);

    // Position-indexed array, not a {variantId: qty} map. With split lines the map form
    // is ambiguous and Shopify mis-applies it (asking for 4 produced 5). The array form
    // addresses each line exactly: put the whole amount on the first matching line and
    // zero the rest, in a single request so nothing re-splits mid-update.
    //
    // The current quantities and this row's line positions are already in the markup,
    // so no /cart.js round trip is needed before sending the update.
    var container = document.querySelector('.cart__items[data-line-qtys]');
    var positions = (row.getAttribute('data-merged-lines') || '')
      .split(',').map(Number).filter(function (n) { return n > 0; });

    if (!container || !positions.length) return Promise.resolve();

    var updates = (container.getAttribute('data-line-qtys') || '')
      .split(',').map(Number);

    positions.forEach(function (pos, idx) {
      updates[pos - 1] = idx === 0 ? target : 0;
    });

    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('Could not update cart');
        return r.json();
      })
      .then(function (cart) {
        // Redraw and refresh the bundle bar together rather than one after the other.
        document.dispatchEvent(new CustomEvent('cart:build'));
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
        refreshBundleProgress();
      })
      .catch(function () {
        // Fall back to a reload so the shopper never sees a cart that disagrees with itself.
        window.location.reload();
      });
  }

  function initMergedCartRows() {
    document.addEventListener('click', function (evt) {
      var row = evt.target.closest('[data-merged-variant]');
      if (!row) return;

      var remove = evt.target.closest('[data-merged-remove]');
      if (remove) {
        evt.preventDefault();
        row.classList.add('is-updating');
        setMergedQuantity(row, 0);
        return;
      }

      var adjust = evt.target.closest('[data-merged-qty-adjust]');
      if (!adjust) return;
      evt.preventDefault();
      evt.stopPropagation();

      var input = row.querySelector('[data-merged-qty-input]');
      var current = parseInt(input && input.value, 10);
      if (isNaN(current)) return;

      var delta = parseInt(adjust.getAttribute('data-merged-qty-adjust'), 10) || 0;
      var next = Math.max(0, current + delta);
      if (next === current) return;

      // Show the new number straight away; the request catches up behind it.
      if (input) input.value = next;
      row.classList.add('is-updating');

      setMergedQuantity(row, next);
    }, true);
  }

  /* ------------------------------------------------------------------
     2. Sticky add to cart (mobile)
     ------------------------------------------------------------------ */
  // While a theme is unpublished, Shopify pins its own preview bar to the bottom of the
  // screen, directly over the buy bar. Measure it and sit above it. On a published theme the
  // bar does not exist, the value is 0, and nothing moves.
  function measurePreviewBar() {
    var pbar = document.querySelector('#PBarNextFrameWrapper, #preview-bar-iframe');
    var height = pbar ? Math.round(pbar.getBoundingClientRect().height) : 0;
    document.documentElement.style.setProperty('--jjPreviewBar', height + 'px');
  }

  function initStickyAtc() {
    var bar = document.querySelector('[data-sticky-atc]');
    if (!bar) return;

    measurePreviewBar();
    window.addEventListener('resize', measurePreviewBar, { passive: true });

    var realButton = document.querySelector('[data-add-to-cart]');
    var stickyButton = bar.querySelector('[data-sticky-atc-button]');
    if (!realButton || !stickyButton) return;

    // Open the picker instead of adding straight away — the shopper chooses the size rather
    // than having whatever variant the page defaulted to added for them. Anything they have
    // already selected higher up the page is carried in as the starting choice.
    stickyButton.addEventListener('click', function (evt) {
      evt.preventDefault();
      if (stickyButton.disabled) return;

      var handle = bar.getAttribute('data-product-handle');
      if (!handle) return;

      var idInput = document.querySelector('[data-add-to-cart]');
      var form = idInput && idInput.closest('form');
      var selectedId = form && form.querySelector('[name="id"]') && form.querySelector('[name="id"]').value;

      QuickAdd.open(handle, bar.getAttribute('data-collection'), selectedId);
    });

    // The bar is only ever disabled when the whole product is sold out (set in Liquid).
    // It deliberately does not mirror the buy button's per-variant state: a sold-out default
    // size must still let the shopper open the picker and choose one that is in stock.

    // Show the bar once the real button has scrolled out of view. Deliberately a scroll check
    // that re-queries the button rather than an IntersectionObserver: the theme re-renders the
    // buy buttons on variant change, which detaches the observed node and silently stops it.
    // Anything covering the screen (cart drawer, quick add, or an app popup such as the
    // Alia email capture) should own the view on its own — the buy bar stands down.
    var overlayIsOpen = function () {
      if (document.documentElement.classList.contains('js-drawer-open')) return true;
      if (document.querySelector('.jj-quickadd.is-open')) return true;

      var appPopup = document.querySelector('[id^="alia-root-"], [id^="alia-popup-root-"]');
      if (appPopup && appPopup.getBoundingClientRect().height > 120) return true;

      return false;
    };

    var ticking = false;
    var updateVisibility = function () {
      ticking = false;
      var btn = document.querySelector('[data-add-to-cart]');
      if (!btn) return;
      var rect = btn.getBoundingClientRect();
      var isOutOfView = rect.bottom < 0 || rect.top > window.innerHeight;
      var shouldShow = isOutOfView && !overlayIsOpen();

      bar.classList.toggle('is-visible', shouldShow);
      // Lets CSS lift app badges (e.g. Alia's "Get 15% OFF") clear of the bar on mobile.
      document.body.classList.toggle('jj-sticky-atc-visible', shouldShow);
    };
    var onScroll = function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(updateVisibility);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    // App popups are injected at the end of body, so watch for them arriving/leaving.
    var overlayTimer;
    new MutationObserver(function () {
      clearTimeout(overlayTimer);
      overlayTimer = setTimeout(function () {
        measurePreviewBar();
        updateVisibility();
      }, 150);
    }).observe(document.body, { childList: true });
    new MutationObserver(updateVisibility)
      .observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

    updateVisibility();

    // Keep the price in step with variant changes. Impulse has no variant-change event, so
    // mirror the real price node directly — that also keeps currency conversion correct.
    var realPrice = document.querySelector('[data-product-price]');
    var stickyPrice = bar.querySelector('[data-sticky-atc-price]');
    if (realPrice && stickyPrice) {
      var syncPrice = function () {
        var text = realPrice.textContent.trim();
        if (text) stickyPrice.textContent = text;
      };
      syncPrice();
      new MutationObserver(syncPrice).observe(realPrice, {
        childList: true, subtree: true, characterData: true
      });
    }
  }

  /* ------------------------------------------------------------------
     3. Quick add drawer
     ------------------------------------------------------------------ */
  var QuickAdd = {
    product: null,
    selectedVariant: null,

    els: function () {
      return {
        backdrop: document.querySelector('[data-quickadd-backdrop]'),
        drawer: document.querySelector('[data-quickadd]'),
        image: document.querySelector('[data-quickadd-image]'),
        title: document.querySelector('[data-quickadd-title]'),
        price: document.querySelector('[data-quickadd-price]'),
        sizes: document.querySelector('[data-quickadd-sizes]'),
        submit: document.querySelector('[data-quickadd-submit]'),
        error: document.querySelector('[data-quickadd-error]'),
        recs: document.querySelector('[data-quickadd-recs]'),
        recsList: document.querySelector('[data-quickadd-recs-list]')
      };
    },

    open: function (handle, collectionHandle, preselectVariantId) {
      var e = this.els();
      if (!e.drawer) return;

      this.selectedVariant = null;
      this.selected = {};
      this.collectionHandle = collectionHandle || '';
      this.preselectVariantId = preselectVariantId || null;
      if (e.error) e.error.textContent = '';
      if (e.sizes) e.sizes.innerHTML = '';
      if (e.recs) e.recs.hidden = true;
      if (e.submit) {
        e.submit.disabled = true;
        e.submit.textContent = 'Select options';
      }

      e.backdrop.classList.add('is-open');
      e.drawer.classList.add('is-open');
      document.body.style.overflow = 'hidden';

      fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : Promise.reject(); })
        .then(this.render.bind(this))
        .catch(function () {
          // If anything goes wrong, send the customer to the product page rather than trapping them.
          window.location.href = '/products/' + handle;
        });
    },

    close: function () {
      var e = this.els();
      if (!e.drawer) return;
      e.backdrop.classList.remove('is-open');
      e.drawer.classList.remove('is-open');
      document.body.style.overflow = '';
    },

    render: function (product) {
      this.product = product;
      var e = this.els();
      var self = this;

      if (e.title) e.title.textContent = product.title;
      if (e.price) e.price.textContent = money(product.price);
      if (e.image && product.featured_image) {
        e.image.src = product.featured_image.replace(/(\.[a-z]+)(\?|$)/i, '_200x$1$2');
        e.image.alt = product.title;
      }

      // Single-variant products need no picker at all.
      if (product.variants.length === 1) {
        this.selectedVariant = product.variants[0];
        if (e.submit) {
          e.submit.disabled = !product.variants[0].available;
          e.submit.textContent = product.variants[0].available ? 'Add to cart' : 'Sold out';
        }
        this.loadRecommendations(product.id);
        return;
      }

      // One row per option (Colour, Size...), the same shape as the product page —
      // not one button per variant combination.
      var optionNames = (product.options || []).map(function (o, i) {
        if (typeof o === 'string') return o;
        return (o && o.name) || 'Option ' + (i + 1);
      });

      this.groups = optionNames.map(function (name, i) {
        var values = [];
        product.variants.forEach(function (v) {
          if (values.indexOf(v.options[i]) === -1) values.push(v.options[i]);
        });
        return { index: i, name: name, values: values };
      });

      // An option with a single value is decided for the shopper rather than shown.
      this.groups.forEach(function (group) {
        if (group.values.length === 1) self.selected[group.index] = group.values[0];
      });

      // Carry over whatever was already chosen on the product page, so opening the picker
      // from the sticky bar is one tap rather than starting from scratch.
      if (this.preselectVariantId) {
        var current = product.variants.filter(function (v) {
          return String(v.id) === String(self.preselectVariantId);
        })[0];
        if (current && current.available) {
          current.options.forEach(function (value, i) { self.selected[i] = value; });
        }
      }

      e.sizes.innerHTML = '';
      this.groups.forEach(function (group) {
        if (group.values.length < 2) return;

        var wrap = document.createElement('div');
        wrap.className = 'jj-quickadd__group';

        var label = document.createElement('span');
        label.className = 'jj-quickadd__label';
        label.textContent = group.name;
        wrap.appendChild(label);

        var row = document.createElement('div');
        row.className = 'jj-quickadd__values';

        group.values.forEach(function (value) {
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'jj-quickadd__size';
          btn.textContent = value;
          btn.setAttribute('data-opt-index', group.index);
          btn.setAttribute('data-opt-value', value);
          btn.addEventListener('click', function () {
            if (btn.disabled) return;
            self.selected[group.index] = value;
            self.pruneUnavailable(group.index);
            self.refreshOptions();
          });
          row.appendChild(btn);
        });

        wrap.appendChild(row);
        e.sizes.appendChild(wrap);
      });

      this.refreshOptions();
      this.loadRecommendations(product.id);
    },

    // True when some in-stock variant has `value` at `index`, constrained only by the options
    // *before* it. Matching the product page: colour is always freely pickable and size is
    // filtered by the chosen colour, rather than a chosen size locking colours out.
    hasStockFor: function (index, value) {
      var self = this;
      return this.product.variants.some(function (v) {
        if (v.options[index] !== value) return false;
        for (var key in self.selected) {
          var k = parseInt(key, 10);
          if (k >= index) continue;
          if (v.options[k] !== self.selected[k]) return false;
        }
        return v.available;
      });
    },

    // Changing an earlier option can invalidate a later one (picking a colour that has no XL),
    // so clear those rather than leave a sold-out value highlighted.
    pruneUnavailable: function (changedIndex) {
      var self = this;
      Object.keys(this.selected).forEach(function (key) {
        var k = parseInt(key, 10);
        if (k <= changedIndex) return;
        if (!self.hasStockFor(k, self.selected[k])) delete self.selected[k];
      });
    },

    refreshOptions: function () {
      var self = this;
      var e = this.els();
      var product = this.product;

      Array.prototype.forEach.call(e.sizes.querySelectorAll('[data-opt-index]'), function (btn) {
        var index = parseInt(btn.getAttribute('data-opt-index'), 10);
        var value = btn.getAttribute('data-opt-value');
        btn.disabled = !self.hasStockFor(index, value);
        btn.classList.toggle('is-selected', self.selected[index] === value);
      });

      var optionCount = (product.options || []).length;
      var chosen = Object.keys(this.selected).length;

      this.selectedVariant = chosen === optionCount
        ? product.variants.filter(function (v) {
            return Object.keys(self.selected).every(function (key) {
              return v.options[parseInt(key, 10)] === self.selected[key];
            });
          })[0] || null
        : null;

      if (this.selectedVariant) {
        if (e.price) e.price.textContent = money(this.selectedVariant.price);
        if (this.selectedVariant.featured_image && e.image) {
          e.image.src = this.selectedVariant.featured_image.src.replace(/(\.[a-z]+)(\?|$)/i, '_200x$1$2');
        }
      }

      if (e.submit) {
        if (this.selectedVariant && this.selectedVariant.available) {
          e.submit.disabled = false;
          e.submit.textContent = 'Add to cart';
        } else {
          e.submit.disabled = true;
          var remaining = this.groups
            .filter(function (g) { return g.values.length > 1 && self.selected[g.index] === undefined; })
            .map(function (g) { return g.name.toLowerCase(); });
          e.submit.textContent = remaining.length
            ? 'Select ' + remaining.join(' and ')
            : 'Sold out';
        }
      }
      if (e.error) e.error.textContent = '';
    },

    loadRecommendations: function (productId) {
      var e = this.els();
      var self = this;
      if (!e.recsList) return;

      // Prefer the collection the shopper is actually browsing — for a drop store that keeps
      // them inside the capsule. Falls back to Shopify's related products off a product page.
      var fromCollection = !!this.collectionHandle;
      var url = fromCollection
        ? '/collections/' + this.collectionHandle + '/products.json?limit=8'
        : '/recommendations/products.json?product_id=' + productId + '&limit=4&intent=related';

      fetch(url, { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : Promise.reject(); })
        .then(function (data) {
          var items = (data.products || [])
            .filter(function (p) { return String(p.id) !== String(productId); })
            .slice(0, 3)
            .map(function (p) {
              // The two endpoints disagree on shape: collection JSON gives decimal price
              // strings and an images array, recommendations gives cents and featured_image.
              var cents = fromCollection
                ? Math.round(parseFloat((p.variants && p.variants[0] && p.variants[0].price) || 0) * 100)
                : p.price;
              var image = fromCollection
                ? (p.images && p.images[0] && (p.images[0].src || p.images[0]))
                : (p.featured_image && (p.featured_image.src || p.featured_image));
              return {
                title: p.title,
                href: p.url || '/products/' + p.handle,
                price: cents,
                image: image || ''
              };
            });

          if (!items.length) return;

          e.recsList.innerHTML = '';
          items.forEach(function (item) {
            var a = document.createElement('a');
            a.className = 'jj-quickadd__rec';
            a.href = item.href;

            if (item.image) {
              var img = document.createElement('img');
              img.src = item.image;
              img.alt = '';
              img.loading = 'lazy';
              a.appendChild(img);
            }

            var text = document.createElement('span');
            var name = document.createElement('span');
            name.className = 'jj-quickadd__rec-name';
            name.textContent = item.title;
            var price = document.createElement('span');
            price.className = 'jj-quickadd__rec-price';
            price.textContent = money(item.price);

            text.appendChild(name);
            text.appendChild(document.createElement('br'));
            text.appendChild(price);
            a.appendChild(text);
            e.recsList.appendChild(a);
          });

          var heading = document.querySelector('[data-quickadd-recs] .jj-quickadd__recs-title');
          if (heading) heading.textContent = fromCollection ? 'More from this collection' : 'You might like too';
          e.recs.hidden = false;
        })
        .catch(function () {});
    },

    add: function () {
      var e = this.els();
      if (!this.selectedVariant) {
        if (e.error) e.error.textContent = 'Please choose your options first.';
        return;
      }

      e.submit.disabled = true;
      e.submit.textContent = 'Adding...';

      fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: this.selectedVariant.id, quantity: 1 }] })
      })
        .then(function (r) {
          return r.json().then(function (body) {
            if (!r.ok) throw new Error(body.description || body.message || 'Could not add to cart');
            return body;
          });
        })
        .then(function () {
          QuickAdd.close();
          // Theme rebuilds the cart and opens the drawer on this event.
          document.dispatchEvent(new CustomEvent('ajaxProduct:added'));
        })
        .catch(function (err) {
          if (e.error) e.error.textContent = err.message;
          e.submit.disabled = false;
          e.submit.textContent = 'Add to cart';
        });
    }
  };

  function initQuickAdd() {
    if (!document.querySelector('[data-quickadd]')) return;

    document.addEventListener('click', function (evt) {
      var trigger = evt.target.closest('[data-quickadd-trigger]');
      if (trigger) {
        evt.preventDefault();
        QuickAdd.open(
          trigger.getAttribute('data-product-handle'),
          trigger.getAttribute('data-collection')
        );
        return;
      }
      if (evt.target.closest('[data-quickadd-close]') || evt.target.closest('[data-quickadd-backdrop]')) {
        QuickAdd.close();
        return;
      }
      if (evt.target.closest('[data-quickadd-submit]')) {
        evt.preventDefault();
        QuickAdd.add();
      }
    });

    document.addEventListener('keydown', function (evt) {
      if (evt.key === 'Escape') QuickAdd.close();
    });
  }

  function init() {
    initStickyAtc();
    initQuickAdd();
    initMergedCartRows();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
